// ============================================================================
// Slash command picker
// ============================================================================
import { cmdOverlay, cmdInput, cmdList } from "./dom.js";
import { escapeHtml } from "./markdown.js";

// Dependencies — set by initSlashCommandPicker
let availableCommandsRef = () => [];
let availableModesRef = () => [];
let modeCommandAliasesRef = {};
let inputRef = () => null;
let insertTextRef = null;
let sendRef = null;

let cmdSelectedIndex = -1;
let cmdFilteredItems = [];

function getCommandList() {
  // Build a combined list from available commands (from ACP) and
  // hardcoded mode commands from the CLI docs.
  const cmds = [];
  const availableModes = availableModesRef();
  const availableCommands = availableCommandsRef();
  const MODE_COMMAND_ALIASES = modeCommandAliasesRef;

  // Add mode-change commands from available modes
  for (const m of availableModes) {
    cmds.push({
      name: m.id,
      description: m.description || `Switch to ${m.name} mode`,
      isMode: true,
      modeId: m.id,
      hint: "instant",
    });
  }

  // Add hardcoded mode aliases that aren't already in availableModes
  const modeIds = new Set(availableModes.map(m => m.id.toLowerCase()));
  for (const [alias, modeId] of Object.entries(MODE_COMMAND_ALIASES)) {
    if (!modeIds.has(modeId) && !cmds.find(c => c.name === alias)) {
      cmds.push({
        name: alias,
        description: `Switch to ${modeId} mode`,
        isMode: true,
        modeId,
        hint: "instant",
      });
    }
  }

  // Gateway-level turbo toggle — not an agent mode or ACP command, but
  // always available via /turbo.
  if (!cmds.find(c => c.name === "turbo")) {
    cmds.push({
      name: "turbo",
      description: "Toggle turbo — bypass permissions + auto-continue until done",
      isMode: false,
      hint: "instant",
    });
  }

  // Add commands from available_commands_update
  for (const c of availableCommands) {
    // Skip if already added as a mode command
    if (cmds.find(cmd => cmd.name === c.name)) continue;
    cmds.push({
      name: c.name,
      description: c.description || "",
      isMode: false,
      hint: c.input ? c.input.hint : null,
    });
  }

  // Add common CLI slash commands as fallback if no commands were advertised
  if (cmds.length === 0) {
    const fallback = [
      { name: "bypass", description: "Switch to Bypass mode (auto-approve all actions)", isMode: true, modeId: "bypass", hint: "instant" },
      { name: "normal", description: "Switch to Normal mode (default)", isMode: true, modeId: "normal", hint: "instant" },
      { name: "plan", description: "Switch to Plan mode (read-only planning)", isMode: true, modeId: "plan", hint: "instant" },
      { name: "accept-edits", description: "Switch to Accept Edits mode", isMode: true, modeId: "accept-edits", hint: "instant" },
      { name: "smart", description: "Switch to Smart mode", isMode: true, modeId: "smart", hint: "instant" },
      { name: "mode", description: "Show or switch the current mode" },
      { name: "model", description: "Show or change the current model" },
      { name: "clear", description: "Clear conversation history and start a new session" },
      { name: "compact", description: "Force conversation compaction" },
      { name: "help", description: "Show available slash commands" },
      { name: "ask", description: "Ask a question without making code changes (oneshot)", hint: "<question>" },
      { name: "context", description: "Show context window usage" },
      { name: "usage", description: "Show estimated credit/ACU usage for the session" },
      { name: "stats", description: "Show session statistics" },
      { name: "exit", description: "Exit the application" },
    ];
    cmds.push(...fallback);
  }

  return cmds;
}

export function openCommandPicker(filter) {
  const allCmds = getCommandList();
  const lower = (filter || "").toLowerCase();
  cmdFilteredItems = lower
    ? allCmds.filter(c => c.name.toLowerCase().includes(lower) || (c.description || "").toLowerCase().includes(lower))
    : allCmds;

  cmdSelectedIndex = cmdFilteredItems.length > 0 ? 0 : -1;
  renderCommandPicker();

  // Position the picker above the composer
  const input = inputRef();
  const rect = input.getBoundingClientRect();
  const pickerHeight = Math.min(360, cmdFilteredItems.length * 44 + 50);
  cmdOverlay.style.left = rect.left + "px";
  cmdOverlay.style.bottom = (window.innerHeight - rect.top + 4) + "px";
  cmdOverlay.style.right = "auto";
  // Ensure it doesn't overflow the right edge
  const pickerWidth = Math.min(440, rect.width);
  if (rect.left + pickerWidth > window.innerWidth - 8) {
    cmdOverlay.style.left = (window.innerWidth - pickerWidth - 8) + "px";
  }
  cmdOverlay.classList.remove("hidden");
}

export function closeCommandPicker() {
  cmdOverlay.classList.add("hidden");
  cmdSelectedIndex = -1;
  cmdFilteredItems = [];
}

function renderCommandPicker() {
  cmdList.innerHTML = "";
  if (cmdFilteredItems.length === 0) {
    const empty = document.createElement("div");
    empty.className = "cmd-empty";
    empty.textContent = "No matching commands";
    cmdList.appendChild(empty);
    return;
  }
  for (let i = 0; i < cmdFilteredItems.length; i++) {
    const c = cmdFilteredItems[i];
    const item = document.createElement("div");
    item.className = "cmd-item" + (i === cmdSelectedIndex ? " selected" : "");
    const name = document.createElement("div");
    name.className = "cmd-name";
    name.innerHTML = `<span class="cmd-slash">/</span>${escapeHtml(c.name)}`;
    item.appendChild(name);
    if (c.description) {
      const desc = document.createElement("div");
      desc.className = "cmd-desc";
      desc.textContent = c.description;
      item.appendChild(desc);
    }
    if (c.hint) {
      const hint = document.createElement("div");
      hint.className = "cmd-hint";
      hint.textContent = c.hint;
      item.appendChild(hint);
    }
    item.addEventListener("click", () => {
      selectCommand(c);
    });
    item.addEventListener("mouseenter", () => {
      cmdSelectedIndex = i;
      // Update selected class without full re-render
      cmdList.querySelectorAll(".cmd-item").forEach((el, idx) => {
        el.classList.toggle("selected", idx === i);
      });
    });
    cmdList.appendChild(item);
  }
}

export function navigateCommandPicker(dir) {
  if (cmdFilteredItems.length === 0) return;
  cmdSelectedIndex = (cmdSelectedIndex + dir + cmdFilteredItems.length) % cmdFilteredItems.length;
  // Update selected class
  const items = cmdList.querySelectorAll(".cmd-item");
  items.forEach((el, idx) => el.classList.toggle("selected", idx === cmdSelectedIndex));
  // Scroll selected into view
  const sel = items[cmdSelectedIndex];
  if (sel) sel.scrollIntoView({ block: "nearest" });
}

export function executeSelectedCommand() {
  if (cmdFilteredItems.length === 0 || cmdSelectedIndex < 0) {
    // No selection — send the raw text as a prompt (might be a slash command
    // the agent handles, or just text starting with /)
    closeCommandPicker();
    sendRef();
    return;
  }
  selectCommand(cmdFilteredItems[cmdSelectedIndex]);
}

function selectCommand(c) {
  closeCommandPicker();
  // Build the command text — include any extra args the user typed
  const input = inputRef();
  const fullText = input.value;
  const parts = fullText.slice(1).split(/\s+/);
  const extraArgs = parts.slice(1).join(" ");
  const cmdText = extraArgs ? `/${c.name} ${extraArgs}` : `/${c.name}`;

  input.value = cmdText;
  sendRef();
}

export function isCommandPickerOpen() {
  return !cmdOverlay.classList.contains("hidden");
}

/**
 * Initialize the slash command picker.
 * @param {object} deps
 * @param {() => array} deps.availableCommands
 * @param {() => array} deps.availableModes
 * @param {object} deps.modeCommandAliases
 * @param {() => HTMLElement} deps.input
 * @param {(text: string) => void} deps.insertText
 * @param {(msg?: any) => void} deps.send
 */
export function initSlashCommandPicker(deps) {
  availableCommandsRef = deps.availableCommands;
  availableModesRef = deps.availableModes;
  modeCommandAliasesRef = deps.modeCommandAliases;
  inputRef = deps.input;
  insertTextRef = deps.insertText;
  sendRef = deps.send;
}
